import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { Search, X, Check, Home, Navigation, MapPin, LocateFixed, ChevronDown, Printer, AlertTriangle } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { CompactHeader } from '../../components/OrellaChatHeader'
import { OverlayPortal } from '../../components/ModalPortal'
import { DoctorLocationBar, DoctorViewToggle } from '../../components/doctors/DoctorSearchKit'
import DoctorsMap from '../doctors/DoctorsMap'
import { providersAPI, doctorsAPI } from '../../services/api'
import { formatSpecialtyName } from '../../utils/specialtyResolver'
import { nearestZip5 } from '../../utils/zip5Locate'
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import { specialtyChips, searchParams } from '../../utils/caseReport'
import orellaO from '../../assets/logos/orella_o_white.png'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LBLUE = '#A7D9FC'
const ICE = '#E9F8FF'
const BODY = '#5A7185'
const MUTE = '#93A5B4'
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || 'AIzaSyCJNsyeMsI4oYPOri_LV1s5JXurYpFzBu4'
/** The radius steps the Distance chip offers and the "search wider" button walks up. */
export const FINDER_RADII = [10, 25, 50, 100]
/** Fewer results than this inside the radius and the finder offers the next radius up. */
const FEW = 8

/**
 * FindDoctorScreen — s98 Phase 6 (D464, D468), reworked at D469 (Dr. Rami 2026-10-10: "need to
 * easily expand search area"; "the share by fax needs to be searchable like [the Orella doctor
 * search]"). Every US doctor in the CMS NPPES registry (GET /providers/search, D466).
 *
 * The same controls as the Orella doctor picker (ShareScreen's find-doctor step):
 *   - a name / practice search box;
 *   - "SHOWING DOCTORS NEAR" (DoctorLocationBar) with its sheet: Home ZIP, current location, or
 *     a typed ZIP. ★ The device fix never leaves the phone (PROD-655): only its ZIP3 area goes to
 *     the server as "<ZIP3>00", which the API resolves to that area's central ZCTA;
 *   - a Distance chip (10 / 25 / 50 / 100 mi), the specialty chips, and "Fax listed";
 *   - List / Map (DoctorViewToggle); the map fits every pin, so a wider radius is a wider map;
 *   - ★ when fewer than FEW doctors are inside the radius, one tap searches the next radius up.
 * Doctors on Orella are listed first and carry the O mark. An Orella doctor goes to today's
 * in-app send, anyone else to the fax screen.
 * ★ s99 (Dr. Rami 2026-10-10: "clicking the options should open the next screen"): a tap on a
 *   doctor's row IS the choice — the row flashes for 120 ms and the next screen opens. No sticky
 *   "Send my report to…" button to scroll for. On the map a pin shows that doctor's row under the
 *   map; tapping the row goes on. The send screen holds the consent, so a tap never sends.
 */
export default function FindDoctorScreen() {
  const navigate = useNavigate()
  const currentCase = useCaseStore((s) => s.currentCase)
  const homeZip = useProfileStore((s) => s.profile?.identification?.zipCode || '')
  const { t } = useI18n()
  const chips = useMemo(() => specialtyChips(currentCase), [currentCase?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const [chip, setChip] = useState(chips[0]?.id || 'all')
  const [text, setText] = useState('')
  const [view, setView] = useState('list')
  const [radius, setRadius] = useState(25)
  const [faxOnly, setFaxOnly] = useState(false)
  const [loc, setLoc] = useState({ source: 'home', zip: '', manual: '' }) // source: home | current | manual
  const [showLocSheet, setShowLocSheet] = useState(false)
  const [showDistance, setShowDistance] = useState(false)
  const closeSheets = () => { setShowLocSheet(false); setShowDistance(false) }
  const [locBusy, setLocBusy] = useState(false)
  const [locError, setLocError] = useState('')
  const [state, setState] = useState({ loading: false, error: '', results: [], zip: '', center: null })
  const [picked, setPicked] = useState(null)
  const [going, setGoing] = useState(false)
  const seq = useRef(0)

  const prefixes = (chips.find((c) => c.id === chip) || {}).prefixes || []
  const activeZip = loc.source === 'current' ? loc.zip : loc.source === 'manual' ? loc.manual : homeZip
  const nameSearch = !!text.trim() && !/^\d{5}(-\d{4})?$/.test(text.trim())

  useEffect(() => {
    const p = searchParams(text, activeZip, prefixes)
    if (!p.zip && !(p.q && p.q.length >= 3)) {
      setState({ loading: false, error: text ? '' : 'needZip', results: [], zip: '', center: null })
      return
    }
    const mine = ++seq.current
    setState((s) => ({ ...s, loading: true, error: '' }))
    const h = setTimeout(async () => {
      try {
        const r = await providersAPI.search({ ...p, radiusMiles: radius, limit: 50 })
        if (mine !== seq.current) return
        setState({ loading: false, error: '', results: Array.isArray(r?.results) ? r.results : [],
                   zip: r?.zip || p.zip || '', center: r?.center || null })
      } catch (e) {
        if (mine !== seq.current) return
        setState({ loading: false, error: e?.status === 503 ? 'unavailable' : 'failed', results: [], zip: '', center: null })
      }
    }, 350)
    return () => clearTimeout(h)
  }, [text, chip, activeZip, radius]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!currentCase) return <Navigate to="/home" replace />

  const shown = faxOnly ? state.results.filter((r) => r.onOrella || r.faxOnFile) : state.results
  const orella = shown.filter((r) => r.onOrella)
  const others = shown.filter((r) => !r.onOrella)
  const sel = state.results.find((r) => r.npi === picked) || null
  const chipLabel = (c) => c.id === 'area' ? formatSpecialtyName(c.areaId, t)
    : c.id === 'primary' ? t('findDoctor.primaryCare', 'Primary care') : t('findDoctor.all', 'All doctors')
  const nextRadius = FINDER_RADII.find((m) => m > radius) || null
  const offerWider = !nameSearch && !state.loading && !state.error && nextRadius && shown.length < FEW
  const locLabel = loc.source === 'current'
    ? t('doctors.currentLocation', 'Current location')
    : loc.source === 'manual'
      ? (loc.manual || '-')
      : (homeZip ? `${homeZip} · ${t('doctors.home', { defaultValue: 'Home' })}` : t('share.searchingNoLocation', 'No location set'))

  async function go(r) {
    if (!r || going) return
    setPicked(r.npi)
    setGoing(true)
    await new Promise((res) => setTimeout(res, 120)) // the row's pressed state, then the next screen
    if (r.onOrella && r.doctorId) {
      try {
        const d = await doctorsAPI.get(r.doctorId)
        const doc = d?.doctor || d
        if (doc && doc.id) { navigate('/case/share', { state: { selectedDoctor: doc } }); return }
      } catch (e) { /* falls through to the fax screen below */ }
    }
    navigate('/case/send-report', { state: { provider: r } })
  }

  async function useCurrentLocation() {
    setLocError('')
    if (!navigator.geolocation) {
      setLocError(t('share.geolocationNotSupported', 'Your browser does not support location services.'))
      return
    }
    setLocBusy(true)
    try {
      const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject,
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }))
      // s101 Phase 4 (x_24): the nearest 5-digit ZIP, picked on the phone (utils/zip5Locate.js).
      const near = await nearestZip5(pos.coords.latitude, pos.coords.longitude, providersAPI.zipAreas)
      if (!near) setLocError(t('share.locationLookupFailed', "Couldn't determine your area from your location."))
      else setLoc((l) => ({ ...l, source: 'current', zip: near.zip }))
    } catch (err) {
      setLocError(err && err.code === 1
        ? t('share.locationDenied', 'Location permission denied. Showing doctors near your home address.')
        : t('share.locationFailed', "Couldn't get your current location. Showing doctors near your home address."))
    } finally {
      setLocBusy(false)
      closeSheets()
    }
  }

  const mapDoctors = shown.filter((r) => typeof r.lat === 'number' && typeof r.lng === 'number')
    .map((r, i) => {
      // Many offices share one ZIP centroid: spread them on a small ring so each pin can be tapped.
      const k = mapDoctors_jitter(i)
      return { ...r, id: r.npi, practice_lat: r.lat + k[0], practice_lng: r.lng + k[1] }
    })
  const center = state.center && typeof state.center.lat === 'number'
    ? { lat: state.center.lat, lng: state.center.lng }
    : (mapDoctors.length ? { lat: mapDoctors[0].lat, lng: mapDoctors[0].lng } : null)

  const pill = (on) => (on ? { background: NAVY, color: '#fff', border: `1px solid ${NAVY}` } : { background: '#fff', color: NAVY, border: '1px solid #E5EDF3' })

  const Row = ({ r }) => (
    <button type="button" onClick={() => go(r)} disabled={going} data-cta="find-doctor-row"
            className="flex gap-3 items-start w-full text-left rounded-2xl p-3 bg-white active:scale-[0.99] transition-transform"
            style={picked === r.npi ? { border: `1.5px solid ${BLUE}`, background: '#F5FBFF' } : { border: '1px solid #E5EDF3' }}>
      <span className="relative w-[42px] h-[42px] rounded-full grid place-items-center flex-none text-[13px] font-extrabold" style={{ background: '#DCEBF7', color: NAVY }}>
        {initials(r.name)}
        {r.onOrella && (
          <em className="absolute grid place-items-center rounded-full" style={{ right: -4, bottom: -4, width: 20, height: 20, background: NAVY }}>
            <img src={orellaO} alt="" style={{ width: 12, height: 12 }} />
          </em>
        )}
      </span>
      <span className="flex-1 min-w-0">
        <b className="block text-[14.5px] font-bold" style={{ color: NAVY }}>{r.name}{r.credential ? `, ${r.credential}` : ''}</b>
        <small className="block text-[12px] leading-snug" style={{ color: BODY }}>
          {[r.specialty, [r.address?.city, r.address?.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
        </small>
        {r.onOrella ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 mt-1.5" style={{ color: '#1F5F99', background: ICE, border: `1px solid ${LBLUE}` }}>
            {t('findDoctor.onOrella', 'On Orella · sent in the app')}
          </span>
        ) : r.faxOnFile ? (
          <span className="inline-block text-[11px] font-bold rounded-full px-2 py-0.5 mt-1.5" style={{ color: BODY, background: '#F2F6FA' }}>{t('findDoctor.faxOnFile', 'Fax on file')}</span>
        ) : (
          <span className="inline-block text-[11px] font-bold rounded-full px-2 py-0.5 mt-1.5" style={{ color: '#B45309', background: '#FFF7E8' }}>{t('findDoctor.noFax', 'No fax listed')}</span>
        )}
      </span>
      {going && picked === r.npi
        ? <OrellaSpinner size={16} className="flex-none" />
        : typeof r.miles === 'number' && <span className="text-[12px] font-bold flex-none" style={{ color: MUTE }}>{t('findDoctor.miles', { defaultValue: '{{n}} mi', n: r.miles })}</span>}
    </button>
  )

  const Wider = () => offerWider ? (
    <div className="rounded-xl p-3 flex items-start gap-2.5" style={{ background: '#FEF3C7', border: '1px solid #FCD34D' }} data-testid="finder-widen">
      <AlertTriangle size={15} className="flex-none mt-0.5" style={{ color: '#92400E' }} />
      <div className="flex-1 min-w-0 text-[12.5px] leading-snug" style={{ color: '#78350F' }}>
        {shown.length === 0
          ? t('findDoctor.noneWithin', { defaultValue: 'No doctors within {{n}} mi.', n: radius })
          : t('findDoctor.fewWithin', { defaultValue: 'Only {{count}} doctors within {{n}} mi.', count: shown.length, n: radius })}
        <button type="button" onClick={() => setRadius(nextRadius)} className="block mt-1.5 font-bold underline" style={{ color: '#92400E' }} data-cta="finder-widen">
          {t('findDoctor.widen', { defaultValue: 'Search within {{n}} mi', n: nextRadius })}
        </button>
      </div>
    </div>
  ) : null

  return (
    <div className="min-h-full bg-gray-50 flex flex-col">
      <CompactHeader
        stepLabel={t('shareHub.stepLabel', 'Share')}
        onBack={() => caseBack(navigate)}
        onNotification={() => navigate('/notifications')}
      />
      <div className="flex-1 px-4 pt-3 pb-6 flex flex-col gap-3">
        <h2 className="text-[19px] font-extrabold tracking-tight" style={{ color: NAVY }}>{t('findDoctor.title', 'Find your doctor')}</h2>
        <label className="flex items-center gap-2 rounded-[14px] px-3 h-12 bg-white" style={{ border: '1.5px solid #E5EDF3' }}>
          <Search size={18} style={{ color: MUTE }} />
          <input value={text} onChange={(e) => setText(e.target.value)} inputMode="search" autoComplete="off"
                 aria-label={t('findDoctor.searchName', 'Search by name or practice')} placeholder={t('findDoctor.searchName', 'Search by name or practice')}
                 className="flex-1 min-w-0 bg-transparent outline-none text-[14.5px]" style={{ color: NAVY }} />
          {state.loading && <OrellaSpinner size={16} />}
        </label>
        <div>
          <DoctorLocationBar userLocation={{ label: locLabel }} setShowLoc={setShowLocSheet} />
          {locError && <p className="-mt-2 text-[11px] text-amber-700 px-1">{locError}</p>}
        </div>
        <div className="flex gap-2 items-center overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
          <button type="button" onClick={() => setShowDistance(true)} data-cta="finder-distance"
                  className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 text-white"
                  style={{ background: BLUE }}>
            <LocateFixed size={12} /> {t('doctors.withinMi', { defaultValue: 'Within {{miles}} mi', miles: radius })} <ChevronDown size={12} />
          </button>
          {chips.map((c) => (
            <button key={c.id} type="button" onClick={() => setChip(c.id)}
                    className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0" style={pill(chip === c.id)}>
              {chipLabel(c)}
            </button>
          ))}
          <button type="button" onClick={() => setFaxOnly((v) => !v)} aria-pressed={faxOnly}
                  className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1 flex-shrink-0" style={pill(faxOnly)}>
            <Printer size={11} /> {t('findDoctor.faxListed', 'Fax listed')}
          </button>
        </div>
        {MAPS_KEY ? <DoctorViewToggle viewMode={view} setViewMode={setView} /> : null}

        {state.error === 'needZip' && (
          <p className="text-[13px] text-center" style={{ color: MUTE }}>{t('findDoctor.needZip', "Type a doctor's name, a practice or a ZIP code.")}</p>
        )}
        {(state.error === 'failed' || state.error === 'unavailable') && (
          <p className="text-[13px] text-center" style={{ color: '#B45309' }}>{t('findDoctor.failed', "We couldn't search right now. Please try again.")}</p>
        )}
        {nameSearch && !state.loading && !state.error && shown.length === 0 && (
          <p className="text-[13px] text-center" style={{ color: MUTE }}>{t('findDoctor.none', 'No doctors found. Try another name or ZIP.')}</p>
        )}
        <Wider />

        {view === 'map' && shown.length > 0 && (
          <>
            <div className="rounded-2xl overflow-hidden" style={{ height: 300, border: '1px solid #E5EDF3' }}>
              <DoctorsMap doctors={mapDoctors} center={center} centerPrecise={false} selectedId={picked}
                          onSelect={(d) => setPicked(d.npi)} mapsKey={MAPS_KEY} orellaOf={(d) => !!d.onOrella} />
            </div>
            {sel ? <Row r={sel} /> : <p className="text-[12px] text-center" style={{ color: MUTE }}>{t('findDoctor.tapPin', 'Tap a pin to choose a doctor.')}</p>}
          </>
        )}
        {view === 'list' && orella.length > 0 && (
          <>
            {/* s99 D476: Orella doctors first, under the same heading as the Orella search. */}
            <p className="text-[15px] font-extrabold -mb-1" style={{ color: NAVY }}>{t('share.bookOnOrella', 'Book and manage directly on Orella')}</p>
            {orella.map((r) => <Row key={r.npi} r={r} />)}
          </>
        )}
        {view === 'list' && others.length > 0 && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[.09em] -mb-1" style={{ color: MUTE }}>
              {state.zip ? t('findDoctor.groupNear', { defaultValue: 'Other doctors near {{zip}}', zip: state.zip }) : t('findDoctor.groupOther', 'Other doctors')}
            </p>
            {others.map((r) => <Row key={r.npi} r={r} />)}
            <p className="text-[12px] text-center" style={{ color: MUTE }}>{t('findDoctor.source', 'From the CMS NPPES registry of US doctors.')}</p>
          </>
        )}
      </div>
      {showLocSheet && renderSheet('location')}
      {showDistance && renderSheet('distance')}
    </div>
  )

  function renderSheet(kind) {
    return (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-3 bottom-actions [--ba-min:12px]"
             style={{ animation: 'shareSheetFade 0.18s ease-out' }} onClick={() => closeSheets()}>
          <div className="bg-white rounded-2xl w-full max-w-md p-4 sm:p-5" style={{ animation: 'shareSheetRise 0.26s cubic-bezier(0.2, 0.8, 0.2, 1)' }}
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold" style={{ color: NAVY }}>
                {kind === 'distance' ? t('findDoctor.distanceTitle', 'How far should we search?') : t('share.searchLocationTitle', 'Where should we search?')}
              </h3>
              <button onClick={() => closeSheets()} className="text-gray-400" aria-label={t('common.close', 'Close')}><X size={20} /></button>
            </div>
            {kind === 'distance' ? (
              <div className="grid grid-cols-2 gap-2">
                {FINDER_RADII.map((m) => (
                  <button key={m} type="button" onClick={() => { setRadius(m); closeSheets() }}
                          className="py-3 rounded-xl text-[14px] font-bold"
                          style={radius === m ? { background: ICE, border: `1.5px solid ${BLUE}`, color: NAVY } : { background: '#fff', border: '1.5px solid #E2E8F0', color: NAVY }}>
                    {t('doctors.withinMi', { defaultValue: 'Within {{miles}} mi', miles: m })}
                  </button>
                ))}
              </div>
            ) : (
              <>
                <SheetOption icon={Home} on={loc.source === 'home'} disabled={!homeZip}
                             title={t('share.homeAddress', 'Home address')}
                             sub={homeZip ? `${t('share.fromProfile', 'From your profile')} · ${homeZip}` : t('share.noHomeAddress', 'No home address in profile yet')}
                             onClick={() => { setLoc((l) => ({ ...l, source: 'home' })); setLocError(''); closeSheets() }} />
                <SheetOption icon={Navigation} on={loc.source === 'current'} disabled={locBusy} busy={locBusy}
                             title={t('share.currentLocation', 'Use my current location')}
                             sub={locBusy ? t('share.gettingLocation', 'Getting your location…') : t('share.useGeoHint', "Helpful when you're away from home")}
                             onClick={useCurrentLocation} />
                <ZipEntry initial={loc.manual} on={loc.source === 'manual'} t={t}
                          onUse={(z) => { setLoc((l) => ({ ...l, source: 'manual', manual: z })); setLocError(''); closeSheets() }} />
              </>
            )}
          </div>
        </div>
        </OverlayPortal>
    )
  }
}

function SheetOption({ icon: Icon, on, disabled, busy, title, sub, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled}
            className="w-full flex items-start gap-3 p-3 rounded-xl mb-2 text-left disabled:opacity-50"
            style={{ backgroundColor: on ? ICE : 'white', border: `1.5px solid ${on ? BLUE : '#E2E8F0'}` }}>
      <Icon size={20} style={{ color: BLUE, flexShrink: 0, marginTop: 2 }} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{title}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>
      </div>
      {busy ? <OrellaSpinner size={16} />
        : on ? <Check size={16} style={{ color: BLUE, flexShrink: 0 }} /> : null}
    </button>
  )
}

function ZipEntry({ initial, on, onUse, t }) {
  const [zip, setZip] = useState(initial || '')
  return (
    <div className="p-3 rounded-xl mb-2" style={{ backgroundColor: on ? ICE : 'white', border: `1.5px solid ${on ? BLUE : '#E2E8F0'}` }}>
      <div className="flex items-center gap-2 mb-2">
        <MapPin size={18} style={{ color: BLUE }} />
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{t('share.enterZip', 'Enter a ZIP code')}</div>
      </div>
      <div className="flex gap-2">
        <input type="text" inputMode="numeric" maxLength={5} pattern="[0-9]*" value={zip} placeholder="00000"
               onChange={(e) => setZip((e.target.value || '').replace(/[^0-9]/g, '').slice(0, 5))}
               className="flex-1 px-3 py-2 rounded-lg text-[13px] focus:outline-none" style={{ border: '1.5px solid #E2E8F0', color: NAVY }} />
        <button onClick={() => zip.length === 5 && onUse(zip)} disabled={zip.length !== 5}
                className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white disabled:opacity-50" style={{ backgroundColor: NAVY }}>
          {t('common.use', 'Use')}
        </button>
      </div>
    </div>
  )
}

function initials(name) {
  const parts = String(name || '').replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean)
  return ((parts[0] || '')[0] || '').toUpperCase() + ((parts[parts.length - 1] || '')[0] || '').toUpperCase()
}

// A small deterministic ring (~0.3 mi) for pins that share one ZIP centroid.
function mapDoctors_jitter(i) {
  if (i === 0) return [0, 0]
  const a = (i * 137.5) * Math.PI / 180
  const r = 0.004 * Math.sqrt(i)
  return [r * Math.cos(a), r * Math.sin(a)]
}
